import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Select,
  Input,
  Popconfirm,
  Spin,
  Button,
  Modal,
  Form,
  InputNumber,
  Tag,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getIncentivesAction,
  payIncentiveAction,
  rejectIncentiveAction,
} from './store/incentiveActions';
import { type Incentive, type IncentivePaymentStatus } from './store/incentiveTypes';
import OutlinedButton from '../../../components/ButtonComponent/OutlinedButton';
import './index.scss';

const PAGE_SIZE = 20;

const STATUS_COLOR: Record<IncentivePaymentStatus, string> = {
  NOT_ACTIVATED: 'blue',
  NOT_PAID: 'orange',
  PAID: 'green',
  REJECTED: 'red',
};

export default function IncentivesIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm<{ onlineAmount: number; offlineAmount: number }>();

  const [incentives, setIncentives] = useState<Incentive[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState<boolean>(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payingAmountLimit, setPayingAmountLimit] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const watchedOnline = Form.useWatch('onlineAmount', form) ?? 0;
  const watchedOffline = Form.useWatch('offlineAmount', form) ?? 0;
  const payTotal = watchedOnline + watchedOffline;
  const isPayDisabled = payTotal <= 0 || payTotal > payingAmountLimit;

  // Filter state
  const [branchOptions, setBranchOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [pledgeNumberSearch, setPledgeNumberSearch] = useState<string>('');
  const [agentNameSearch, setAgentNameSearch] = useState<string>('');
  const [agentIdSearch, setAgentIdSearch] = useState<string>('');

  useEffect(() => {
    const stored = localStorage.getItem('branches');
    if (stored != null) {
      const parsed: Array<{ code: string; id: string }> = JSON.parse(stored);
      setBranchOptions(parsed.map((b) => ({ label: b.code, value: b.id })));
    }
  }, []);

  const fetchIncentives = useCallback(
    async (page: number, branchId: string, status: string, pledgeNum: string, agentName: string, agentId: string) => {
      setIsLoading(true);
      const result = await getIncentivesAction(dispatch, {
        branchId: branchId !== '' ? branchId : undefined,
        paymentStatus: status !== '' ? status : undefined,
        pledgeNumber: pledgeNum !== '' ? pledgeNum : undefined,
        agentName: agentName !== '' ? agentName : undefined,
        agentId: agentId !== '' ? agentId : undefined,
        page,
        limit: PAGE_SIZE,
      });
      if (result !== null) {
        setIncentives(result.incentives);
        setTotal(result.total);
      }
      setIsLoading(false);
    },
    [dispatch]
  );

  useEffect(() => {
    void fetchIncentives(1, '', '', '', '', '');
  }, [fetchIncentives]);

  const handleSearch = () => {
    setCurrentPage(1);
    void fetchIncentives(1, selectedBranch, selectedStatus, pledgeNumberSearch, agentNameSearch, agentIdSearch);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    void fetchIncentives(page, selectedBranch, selectedStatus, pledgeNumberSearch, agentNameSearch, agentIdSearch);
  };

  const openPayModal = (id: string, amountToBePaid: number) => {
    setPayingId(id);
    setPayingAmountLimit(amountToBePaid);
    form.resetFields();
    setIsPayModalOpen(true);
  };

  const handlePay = async () => {
    const values = await form.validateFields();
    if (payingId === null) return;

    setIsSubmitting(true);
    const success = await payIncentiveAction(dispatch, {
      id: payingId,
      onlineAmount: values.onlineAmount ?? 0,
      offlineAmount: values.offlineAmount ?? 0,
    });
    setIsSubmitting(false);

    if (success) {
      setIsPayModalOpen(false);
      setPayingId(null);
      void fetchIncentives(currentPage, selectedBranch, selectedStatus, pledgeNumberSearch, agentNameSearch, agentIdSearch);
    }
  };

  const handleReject = async (id: string) => {
    const success = await rejectIncentiveAction(dispatch, id);
    if (success) {
      void fetchIncentives(currentPage, selectedBranch, selectedStatus, pledgeNumberSearch, agentNameSearch, agentIdSearch);
    }
  };

  const columns: TableColumnsType<Incentive> = [
    {
      title: 'Pledge Number',
      dataIndex: 'pledgeNumber',
      key: 'pledgeNumber',
      render: (val: string) => <strong>{val}</strong>,
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_, record) => record.agentId?.name ?? '-',
    },
    {
      title: 'Bank Account',
      key: 'bankAccount',
      render: (_, record) => record.bankAccountId?.name ?? '-',
    },
    {
      title: 'Interest Days',
      key: 'interestDays',
      render: (_, record) => {
        const days = record.paymentStatus === 'NOT_ACTIVATED'
          ? record.realtimeInterestDays
          : record.interestDays;
        return days ?? '-';
      },
    },
    {
      title: 'Amount to be Paid (₹)',
      key: 'amountToBePaid',
      render: (_, record) => {
        const amount = record.paymentStatus === 'NOT_ACTIVATED'
          ? record.realtimeAmountToBePaid
          : record.amountToBePaid;
        return amount != null ? `₹${amount.toLocaleString('en-IN')}` : '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: IncentivePaymentStatus) => (
        <Tag color={STATUS_COLOR[status]}>{status.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: 'Online Amount (₹)',
      key: 'onlineAmount',
      render: (_, record) => (record.onlineAmount != null ? `₹${record.onlineAmount}` : '-'),
    },
    {
      title: 'Offline Amount (₹)',
      key: 'offlineAmount',
      render: (_, record) => (record.offlineAmount != null ? `₹${record.offlineAmount}` : '-'),
    },
    {
      title: 'Pledge Close Date',
      key: 'pledgeCloseDate',
      render: (_, record) => {
        const closedDate =
          typeof record.pledgeId === 'object' && record.pledgeId !== null
            ? record.pledgeId.closedDate
            : undefined;
        return closedDate != null ? new Date(closedDate).toLocaleDateString('en-IN') : '-';
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val: string) => new Date(val).toLocaleDateString('en-IN'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (record.paymentStatus !== 'NOT_PAID') return null;

        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ background: '#DD1E2D', borderColor: '#DD1E2D' }}
              onClick={() => { openPayModal(String(record._id), record.amountToBePaid ?? 0); }}
            >
              Pay
            </Button>
            <Popconfirm
              title="Reject this incentive?"
              description="This action cannot be undone."
              onConfirm={() => { void handleReject(String(record._id)); }}
              okText="Reject"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger>
                Reject
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div className="incentives__page">
      <h1>Incentives</h1>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="customers__input__container">
          <h4>Branch</h4>
          <Select
            style={{ width: 200, height: 56 }}
            placeholder="All branches"
            options={branchOptions}
            allowClear
            onChange={(value: string | undefined) => { setSelectedBranch(value ?? ''); }}
            className="deposit__select"
          />
        </div>
        <div className="customers__input__container">
          <h4>Status</h4>
          <Select
            style={{ width: 160, height: 56 }}
            placeholder="All statuses"
            allowClear
            options={[
              { label: 'Not Activated', value: 'NOT_ACTIVATED' },
              { label: 'Not Paid', value: 'NOT_PAID' },
              { label: 'Paid', value: 'PAID' },
              { label: 'Rejected', value: 'REJECTED' },
            ]}
            onChange={(value: string | undefined) => { setSelectedStatus(value ?? ''); }}
            className="deposit__select"
          />
        </div>
        <div className="customers__input__container">
          <h4>Pledge Number</h4>
          <Input
            style={{ width: 200, height: 56, borderRadius: 12 }}
            placeholder="Search pledge"
            value={pledgeNumberSearch}
            onChange={(e) => { setPledgeNumberSearch(e.target.value); }}
            onPressEnter={handleSearch}
          />
        </div>
        <div className="customers__input__container">
          <h4>Agent Name</h4>
          <Input
            style={{ width: 200, height: 56, borderRadius: 12 }}
            placeholder="Search agent"
            value={agentNameSearch}
            onChange={(e) => { setAgentNameSearch(e.target.value); }}
            onPressEnter={handleSearch}
          />
        </div>
        <div className="customers__input__container">
          <h4>Agent ID</h4>
          <Input
            style={{ width: 200, height: 56, borderRadius: 12 }}
            placeholder="Search agent ID"
            value={agentIdSearch}
            onChange={(e) => { setAgentIdSearch(e.target.value); }}
            onPressEnter={handleSearch}
          />
        </div>
        <OutlinedButton label="Search" onClick={handleSearch} />
      </div>

      <Spin spinning={isLoading}>
        <Table
          dataSource={incentives}
          columns={columns}
          rowKey="_id"
          bordered
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total,
            onChange: handlePageChange,
            showSizeChanger: false,
            showTotal: (t) => `${t} incentives`,
          }}
          locale={{ emptyText: 'No incentives found.' }}
        />
      </Spin>

      <Modal
        title="Pay Incentive"
        open={isPayModalOpen}
        onCancel={() => { setIsPayModalOpen(false); setPayingId(null); }}
        onOk={() => { void handlePay(); }}
        okText="Confirm Payment"
        okButtonProps={{ loading: isSubmitting, disabled: isPayDisabled, style: isPayDisabled ? { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#00000040' } : { background: '#DD1E2D', borderColor: '#DD1E2D' } }}
        cancelButtonProps={{ disabled: isSubmitting }}
        destroyOnClose
        className="incentive__pay__modal"
      >
        <Form form={form} layout="vertical">
          <p style={{ marginBottom: 12, fontWeight: 500 }}>
            Incentive Amount: ₹{payingAmountLimit.toLocaleString('en-IN')}
          </p>
          <Form.Item name="onlineAmount" label="Online Amount (₹)">
            <InputNumber min={0} controls={false} placeholder="0" />
          </Form.Item>
          <Form.Item name="offlineAmount" label="Offline Amount (₹)">
            <InputNumber min={0} controls={false} placeholder="0" />
          </Form.Item>
          {payTotal > payingAmountLimit && (
            <p style={{ color: '#ff4d4f', fontSize: 13 }}>
              Total ₹{payTotal.toLocaleString('en-IN')} exceeds incentive amount of ₹{payingAmountLimit.toLocaleString('en-IN')}.
            </p>
          )}
          {payTotal <= 0 && (
            <p style={{ color: '#94A3B8', fontSize: 13 }}>
              At least one amount must be greater than zero.
            </p>
          )}
        </Form>
      </Modal>
    </div>
  );
}
